import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const CreateLetter = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [activeSubscribers, setActiveSubscribers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get("/newsletter");
        setActiveSubscribers(response.data.stats?.active || 0);
      } catch (err) {
        console.error("Erreur chargement stats newsletter :", err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const response = await api.post("/newsletter/send", { subject, content });
      setMessage(response.data.message || "Newsletter envoyée avec succès !");
      setMessageType("success");
      setSubject("");
      setContent("");
    } catch (err) {
      setMessage(err.response?.data?.message || "Erreur lors de l'envoi.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const contentLength = useMemo(() => content.length, [content]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-6">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-green-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-green-100">
              Newsletter · Envoi libre
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Créer une newsletter</h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Rédigez un message personnalisé et envoyez-le à l'ensemble de vos abonnés actifs.
            </p>
            {!loadingStats && (
              <p className="mt-4 text-sm text-green-100/90">
                {activeSubscribers} abonné{activeSubscribers > 1 ? "s" : ""} actif{activeSubscribers > 1 ? "s" : ""}
              </p>
            )}
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <section className="xl:col-span-2 rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-white">Préparer l'envoi</h2>
                <p className="mt-1 text-sm text-slate-400">Rédigez votre sujet et le contenu à envoyer.</p>
              </div>
              <button
                onClick={() => navigate("/dashboard/newsletter")}
                className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
              >
                Retour
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Sujet</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ex : Nouveau post disponible"
                  required
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-green-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-200">Contenu</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows="10"
                  placeholder="Écris ton message ici..."
                  required
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-green-500"
                />
                <p className="mt-2 text-xs text-slate-500">Nombre de caractères : {contentLength}</p>
              </div>

              {message && (
                <div className={`rounded-xl px-4 py-3 text-sm ${messageType === "success" ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border border-red-500/30 bg-red-500/10 text-red-300"}`}>
                  {message}
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-xl bg-green-600 px-5 py-3 font-semibold text-white transition hover:bg-green-500 disabled:opacity-60"
                >
                  {loading ? "Envoi en cours..." : "Envoyer la newsletter"}
                </button>
                <button
                  type="button"
                  onClick={() => { setSubject(""); setContent(""); setMessage(""); }}
                  className="rounded-xl border border-slate-700 px-5 py-3 font-semibold text-slate-200 transition hover:bg-slate-800"
                >
                  Réinitialiser
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Aperçu de l'envoi</h2>
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-green-400">Destinataires</p>
                <p className="text-sm font-semibold text-white">
                  {loadingStats ? "Chargement..." : `${activeSubscribers} abonné${activeSubscribers > 1 ? "s" : ""} actif${activeSubscribers > 1 ? "s" : ""}`}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-green-400">Sujet</p>
                <p className="text-sm font-semibold text-white">{subject.trim() || "Aucun sujet défini."}</p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-green-400">Message</p>
                <p className="whitespace-pre-line text-sm leading-6 text-slate-300">{content.trim() || "Aucun contenu défini."}</p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CreateLetter;